import React, { useState, useEffect } from 'react';
import { Button as MuiButton, Tooltip, IconButton, Box, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, Divider, TextField, Alert, FormControl, FormHelperText } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { CoreForumCreateReply } from './CoreForumCreateReply';
import { CoreForumEditor } from './CoreForumEditor';
import { parseContentToHtml } from '../../../utils/markdownParser'; // 使用通用 markdown 解析器
import { setPinnedNode, removePinnedNode } from '../../../utils/pinnedNodes'; // 新增
import { convertIdToAlphabet } from '../../../utils/idConverter'; // 引入 ID 轉換器
import { formatTimestamp } from '../../../utils/timeFormatter'; // 時間格式化工具
import UserProfileCard from '../../UserProfileCard';
import NodePreviewDialog from '../../shared/NodePreviewDialog';
import axios from 'axios';
import url from '../../../url.js';
import '../../../styles/NodeContentStyles.css'; // 導入節點內容樣式

export const CoreForumViewNode = ({ open, onClose, nodeContent, ws, groupId, onEditorOpen, onEditorClose, graphNodes, graphEdges, allTagConfigs = {}, buildGroupContext, mode, onRefresh, onNodeUpdated, onNodeDeleted }) => { // 接收 groupId, graphEdges, allTagConfigs, buildGroupContext, mode, onRefresh, onNodeUpdated 和 onNodeDeleted
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // 節點引用預覽狀態
    const [previewNode, setPreviewNode] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    
    // ✅ 检测访客模式
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';

    const openModal = () => {
        setModalOpen(true);
        if (onEditorOpen) onEditorOpen();
    };

    const closeModal = () => {
        setModalOpen(false);
        if (onEditorClose) onEditorClose();
    };

    const handleReply = async (e) => {
        onClose();
        openModal();
    };

    // 檢查是否為作者且在15分鐘內
    const canEditOrDelete = () => {
        if (!nodeContent) return false;
        const currentUserId = localStorage.getItem('userId');
        const nodeAuthorId = nodeContent.authorId;
        
        // 如果節點沒有 authorId（舊數據），不能編輯/刪除
        if (!nodeAuthorId) return false;
        
        // 檢查是否為作者
        if (nodeAuthorId !== parseInt(currentUserId)) return false;
        
        // 檢查是否在15分鐘內
        const nodeCreatedAt = new Date(nodeContent.createdAt);
        const now = new Date();
        const timeDiff = now - nodeCreatedAt;
        const fifteenMinutesInMs = 15 * 60 * 1000;
        
        return timeDiff <= fifteenMinutesInMs;
    };

    const handleEdit = () => {
        if (!nodeContent) return;
        setEditTitle(nodeContent.title || '');
        setEditContent(nodeContent.content || '');
        setEditMode(true);
        setError(null);
    };

    // 當進入編輯模式時，初始化編輯內容
    useEffect(() => {
        if (editMode && nodeContent) {
            setEditTitle(nodeContent.title || '');
            setEditContent(nodeContent.content || '');
        }
    }, [editMode, nodeContent]);

    // 處理節點引用點擊事件
    useEffect(() => {
        if (!open && !showPreview) return;

        const handleNodeReferenceClick = (event) => {
            const target = event.target;
            
            // 檢查是否點擊了節點引用或其子元素
            const nodeReference = target.closest('.node-reference');
            if (!nodeReference) return;
            console.log('nodeReference', nodeReference);
            // 阻止事件冒泡，避免觸發其他點擊事件
            event.preventDefault();
            event.stopPropagation();

            // 獲取節點 ID 和標題
            const nodeId = nodeReference.getAttribute('data-node-id');
            const nodeTitle = nodeReference.getAttribute('data-node-title');

            console.log('節點引用被點擊:', { nodeId, nodeTitle });

            if (nodeId && graphNodes) {
                // 在 graphNodes 中尋找對應的節點
                const referencedNode = graphNodes.find(n => n.id == nodeId);
                console.log('找到的節點:', referencedNode);
                
                if (referencedNode) {
                    setPreviewNode(referencedNode);
                    setShowPreview(true);
                } else {
                    console.warn('未找到節點 ID:', nodeId);
                }
            }
        };

        // 使用 setTimeout 確保 DOM 已經渲染
        const timeoutId = setTimeout(() => {
            // 為所有節點內容容器添加點擊監聽器
            const contentContainers = document.querySelectorAll('.node-content-container');
            console.log('找到的內容容器數量:', contentContainers.length);
            
            if (contentContainers.length > 0) {
                contentContainers.forEach(container => {
                    container.addEventListener('click', handleNodeReferenceClick, true);
                });
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const contentContainers = document.querySelectorAll('.node-content-container');
            contentContainers.forEach(container => {
                container.removeEventListener('click', handleNodeReferenceClick, true);
            });
        };
    }, [open, showPreview, graphNodes, nodeContent]);

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditTitle('');
        setEditContent('');
        setError(null);
        // 如果是在查看模式下取消編輯，保持對話框打開
        // 如果是在編輯模式下取消，關閉對話框
        if (open) {
            // 保持對話框打開，但退出編輯模式
        }
    };

    const handleSaveEdit = async () => {
        if (!nodeContent || !groupId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const jwtToken = localStorage.getItem('jwtToken');
            const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
            
            const response = await axios.put(
                `${url.backendHost}api/nodes/${nodeContent.id}`,
                {
                    title: editTitle,
                    content: editContent,
                    groupId: groupId
                },
                { headers, withCredentials: true }
            );
            
            if (response.status === 200) {
                setEditMode(false);
                // 重新獲取節點列表
                if (onRefresh) {
                    await onRefresh();
                }
                // 通知父組件節點已更新，清除舊的節點內容
                if (onNodeUpdated) {
                    onNodeUpdated(nodeContent.id);
                }
                // 關閉對話框
                onClose();
            }
        } catch (err) {
            console.error('更新節點失敗:', err);
            setError(err.response?.data?.message || '更新節點失敗，請稍後再試');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!nodeContent) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const jwtToken = localStorage.getItem('jwtToken');
            const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
            
            const response = await axios.delete(
                `${url.backendHost}api/nodes/${nodeContent.id}`,
                { headers, withCredentials: true }
            );
            
            if (response.status === 200) {
                setDeleteConfirmOpen(false);
                // 重新獲取節點列表
                if (onRefresh) {
                    await onRefresh();
                }
                // 通知父組件節點已刪除，清除節點內容
                if (onNodeDeleted) {
                    onNodeDeleted(nodeContent.id);
                }
                // 關閉對話框
                onClose();
            }
        } catch (err) {
            console.error('刪除節點失敗:', err);
            setError(err.response?.data?.message || '刪除節點失敗，請稍後再試');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setError(null);
    };

    // 修改 isPinned 判斷，使用 group-specific key
    const pinnedNodes = JSON.parse(localStorage.getItem('pinnedNodes') || '{}');
    const isPinned = nodeContent && nodeContent.id === pinnedNodes[groupId];

    const canEdit = canEditOrDelete();

    return (
        <>
            {/* 編輯模式對話框 - 使用與新增想法相同的設計 */}
            {editMode ? (
                <Dialog
                    open={open && editMode}
                    onClose={handleCancelEdit}
                    maxWidth="lg"
                    fullWidth={true}
                    scroll='body'
                    PaperProps={{
                        sx: {
                            maxHeight: '90vh',
                            height: 'auto'
                        }
                    }}
                >
                    <DialogTitle>編輯想法</DialogTitle>
                    <Divider variant="middle" />
                    <DialogContent sx={{ 
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 200px)'
                    }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* 標題輸入 - 使用與新增想法相同的樣式 */}
                        <FormControl variant="standard" fullWidth>
                            <TextField
                                required
                                id="edit-title"
                                autoFocus
                                margin="dense"
                                label="想法標題"
                                type="text"
                                name='title'
                                value={editTitle}
                                fullWidth
                                variant="outlined"
                                onChange={(e) => setEditTitle(e.target.value)}
                            />
                            <FormHelperText>
                                請為你的想法下一個清楚的標題，讓其他同學能快速了解你的重點
                            </FormHelperText>
                        </FormControl>

                        {/* 內容編輯器 - 使用與新增想法相同的樣式和配置 */}
                        <Box sx={{ 
                            mt: 2,
                            minHeight: '400px',
                            height: '400px',
                            maxHeight: '70vh',
                            resize: 'vertical',
                            overflow: 'hidden',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: '20px',
                                height: '20px',
                                background: 'linear-gradient(135deg, transparent 50%, #ccc 50%)',
                                cursor: 'ns-resize',
                                pointerEvents: 'none',
                                zIndex: 10
                            },
                            // 覆蓋 CoreForumEditor 外層 flex div
                            '& > div': {
                                height: '100% !important',
                                display: 'flex !important',
                                flexDirection: 'row !important',
                                minHeight: 0
                            },
                            '& .editor-container': {
                                margin: '0 !important',
                                height: '100% !important',
                                display: 'flex !important',
                                flexDirection: 'column !important',
                                maxWidth: '100% !important',
                                minHeight: 0,
                                overflow: 'hidden'
                            },
                            '& .editor-inner': {
                                flex: '1 1 auto !important',
                                overflowY: 'auto !important',
                                overflowX: 'hidden !important',
                                minHeight: 0,
                                maxHeight: '100%',
                                position: 'relative'
                            },
                            '& .lexical-toolbar': {
                                flexShrink: 0
                            },
                            '& .editor-input': {
                                minHeight: '200px !important',
                                height: 'auto !important',
                                overflowY: 'auto !important'
                            }
                        }}>
                            <CoreForumEditor 
                                changeFunc={(content) => {
                                    setEditContent(content);
                                }}
                                nodePlan={{
                                    intro: editContent,
                                    saffoldBody: [
                                        "💡這邊我想提供一個我的想法",
                                        "🧐我覺得這邊應該要",
                                        "❓我想知道",
                                        "🙅🏻這不能解釋",
                                        "📄能針對 舉例嗎",
                                        "📄能針對 提供參考資料嗎",
                                        "✍🏻我到目前為止的想法是"
                                    ],
                                    scaffoldWords: [
                                        "這邊我想提供一個我的想法", 
                                        "我覺得這邊應該要", 
                                        "我想知道", 
                                        "這不能解釋", 
                                        "能針對 舉例嗎",
                                        "能針對 提供參考資料嗎", 
                                        "我到目前為止的想法是"
                                    ]
                                }}
                                graphNodes={graphNodes}
                            />
                        </Box>
                    </DialogContent>
                    
                    <DialogActions>
                        <MuiButton onClick={handleCancelEdit}>取消</MuiButton>
                        <LoadingButton 
                            onClick={handleSaveEdit} 
                            loading={loading} 
                            loadingPosition="start" 
                            variant="contained"
                            disabled={!editTitle.trim() || !editContent}
                        >
                            儲存
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            ) : (
                /* 查看模式對話框 */
            <Dialog
                    open={open && !editMode}
                onClose={onClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="md"
                fullWidth={true}
            >
                <DialogTitle id="alert-dialog-title">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        {nodeContent && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        borderRadius: '4px',
                                        padding: '2px 8px',
                                        fontSize: '0.875rem',
                                        fontWeight: 'bold',
                                        minWidth: '32px',
                                        textAlign: 'center'
                                    }}
                                >
                                   #{convertIdToAlphabet(nodeContent.id)}
                                </Box>
                                <span>{nodeContent.title}</span>
                            </Box>
                        )}
                    </Box>
                </DialogTitle>
                <Divider variant="middle" />
                <DialogContent id="nodeWindow">
                    <DialogContentText id="alert-dialog-description">
                        {nodeContent && (
                            <>
                                <div
                                    className="node-content-container"
                                    dangerouslySetInnerHTML={{
                                        __html: parseContentToHtml(nodeContent.content)
                                    }}
                                />
                                <br/>
                                <br/>
                                <br />
                                <div>{nodeContent.authorId && nodeContent.authorId !== parseInt(localStorage.getItem('userId')) ? (
                                        <UserProfileCard
                                            userId={nodeContent.authorId}
                                            userName={nodeContent.author+" 💬"}
                                            isGuestMode={isGuestMode}
                                        />
                                    ) : (
                                        <span>{nodeContent.author}</span>
                                    )} - {formatTimestamp(nodeContent.createdAt)} </div>
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                        {canEdit && (
                            <>
                                <MuiButton
                                    onClick={handleEdit}
                                    variant="text"
                                    color="primary"
                                    sx={{
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    編輯
                                </MuiButton>
                                <MuiButton
                                    onClick={handleDeleteClick}
                                    variant="text"
                                    color="error"
                                    sx={{
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    刪除
                                </MuiButton>
                            </>
                        )}
                    {!isGuestMode && (
                        <MuiButton
                            onClick={handleReply}
                            variant="text"
                            color="primary"
                            sx={{
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            回覆
                        </MuiButton>
                    )}
                    <MuiButton
                        onClick={onClose}
                        variant="text"
                        color="primary"
                        sx={{
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        取消
                    </MuiButton>
                </DialogActions>
            </Dialog>
            )}
            <CoreForumCreateReply
                open={modalOpen}
                onClose={closeModal}
                nodeContent={nodeContent}
                ws={ws}
                graphNodes={graphNodes} // 傳遞完整的圖形節點數據給回覆組件
                graphEdges={graphEdges} // 傳遞邊數據
                allTagConfigs={allTagConfigs} // 傳遞標籤配置
                buildGroupContext={buildGroupContext} // 傳遞 context 構建函數
                mode={mode} // 傳遞模式標識
            />
            
            {/* 刪除確認對話框 */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    確認刪除
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        確定要刪除這個節點嗎？此操作無法復原。
                    </DialogContentText>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <MuiButton
                        onClick={handleDeleteCancel}
                        variant="text"
                        color="primary"
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                        }}
                    >
                        取消
                    </MuiButton>
                    <MuiButton
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                        }}
                    >
                        {loading ? '刪除中...' : '確認刪除'}
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* 節點引用預覽對話框 */}
            <NodePreviewDialog
                open={showPreview}
                onClose={() => {
                    setShowPreview(false);
                    setPreviewNode(null);
                }}
                node={previewNode}
                showUserProfile={true}
                isGuestMode={isGuestMode}
            />
        </>
    );
}

